<script setup>
import {useBaseStore} from "~/stores/base";
import Modal from "../Modal.vue"
import Drawer from "../Drawer.vue"
import ModuleInput from "~/components/ModuleInput.vue";
import {EMAIL_ERROR_TIPS_MAP} from "~/utils/constants";
import {emailValid} from "~/utils/common";
import {userSaveStockNotification} from "~/api/api.user";
import {Loading} from "@element-plus/icons-vue";

const BNTOMessage = useNuxtApp().$BNTOMessage

const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits(["close"])

/** props **/
const props = defineProps({
  product: {
    default: {},
    required: true
  },
  open: {
    type: Boolean,
    default: false,
    required: true
  }
})

/** data **/
const isLoading = ref(false)
const current = ref(0)
const options = ref([])
const email = ref("")
const emailErrorTips = ref("")
const emailError = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const btnDisabled = computed(() => {
  const noSelected = options.value.every(item =>
    item.sizeList.every(size => !size.selected)
  )
  return noSelected || !email.value || emailError.value
})

/** watch **/
watch(() => props.open, () => {
  // 找到没库存的项
  const noInventoryItems = props.product.optionConfig
    ?.filter(option =>
      option.sizeList?.some(size => size.inventory === 0) // 筛选出有 sizeList 且包含 inventory = 0 的项
    )
    .map(option => ({
      ...option, // 保留原有的 option 数据
      sizeList: option.sizeList.filter(size => size.inventory === 0) // 只保留 inventory = 0 的 sizeListItem
        .map(size => ({
          ...size,
          selected: false // 初始化selected为false
        })),
    }));

  // 根据 colorGroup 顺序排序
  // options.value = props.product.colorGroup
  //   .map(color => noInventoryItems.find(option => option.name === color)) // 按 colorGroup 顺序匹配
  //   .filter(Boolean); // 去掉未匹配的颜色
  options.value = noInventoryItems || []
  console.log(options.value)
  // 如果 colorGroup 中的某些颜色在 noInventoryItems 中找不到匹配项，那么 map() 会返回 undefined。
  // 而我们希望结果中只包含有效的 optionConfig，所以需要用 .filter(Boolean) 来清理掉这些无效项。
}, {immediate: true})
watch(userInfo, () => {
  email.value = userInfo.value.email
}, {immediate: true})

/** methods **/
const handleColorSelect = (index) => {
  current.value = index
}
const handleSizeSelect = ({size, index}) => {
  size.selected = !size.selected
}
const handleEmailBlur = () => {
  // reset
  emailError.value = false
  emailErrorTips.value = ""
  // 必填校验
  if (!email.value) {
    emailError.value = true
    emailErrorTips.value = EMAIL_ERROR_TIPS_MAP.REQUIRED
    return
  }
  // 合法校验
  const valid = emailValid(email.value)
  if (!valid) {
    emailError.value = true
    emailErrorTips.value = EMAIL_ERROR_TIPS_MAP.FORMAT_ERROR
  }
}
const notify = async () => {
  if (btnDisabled.value) return
  if (isLoading.value) return
  isLoading.value = true
  const skuList = options.value.flatMap(item =>
    item.sizeList
      .filter(size => size.selected) // 过滤 selected 为 true 的项
      .map(size => size.sku)         // 提取 sku
  )
  await userSaveStockNotification({email: email.value, skuIdList: skuList})
  isLoading.value = false
  reset()
  BNTOMessage.sparkle({
    title: "Subscriptions Confirmed!",
    message: "You’ve successfully subscribed to restock notifications for the selected items. We’ll notify you as soon as they’re back in stock—stay tuned!",
  })
  emits('close')
}
const reset = () => {
  isLoading.value = false
  current.value = 0
  options.value = []
  email.value = userInfo.value.email
  emailErrorTips.value = ""
  emailError.value = false
}
/** lifecycle **/

</script>

<template>
  <div class="modal-back-in-stock-notification-mobile" v-if="isMobile">
    <Drawer :open="open" @close="emits('close')" :btn-close-visible="true" :position="'bottom'" :border-radius="'25px 25px 0 0'" :z-index="4">
      <div class="content">
        <div class="header">
          <div class="title">Back in stock notification</div>
          <div class="desc">Please let us know the size and color you are interested in and we will let you know once
            it's back in stock.
          </div>
        </div>
        <div class="product">
          <div class="subtitle">
            {{ product.brand }}
          </div>
          <div class="title">
            {{ product.title }}
          </div>
        </div>
        <div class="colors">
          <div class="selections">
            <div
              class="selection"
              :class="{selected: current === index}"
              v-for="(item, index) in options"
              @click="handleColorSelect(index)"
            >
              <div class="color" :style="{backgroundColor: item.data}">
                <img v-if="item.data.includes('http')" :src="item.data" alt="">
              </div>
            </div>
          </div>
          <div class="selected">
            Color: <span class="bold">{{ options[current]?.name }}</span>
          </div>
        </div>
        <div class="sizes">
          <div class="title">
            <span>Select a Size:</span>
          </div>
          <div class="selections">
            <div
              v-for="(size, index) in options[current]?.sizeList"
              class="selection"
              :class="{
                large: options[current]?.sizeList.find(size => size.value.length >= 5),
                selected: size.selected
              }"
              @click="handleSizeSelect({size, index})"
            >
              {{ size.value }}
            </div>
          </div>
        </div>
        <div class="email">
          <module-input
            type="text"
            v-model="email"
            title="Email"
            name="email"
            :error-tips="emailErrorTips"
            :error="emailError"
            @blur="handleEmailBlur"
          />
        </div>
        <div class="confirm">
          <div class="btn-primary" :class="{'btn-disabled': btnDisabled || isLoading}" @click="notify">
            <el-icon v-if="isLoading" class="is-loading" color="white" size="16" style="margin-right: 8px">
              <Loading/>
            </el-icon>
            <span>notify me</span>
          </div>
          <div class="tips">
            We cannot guarantee that back in stock requests will be fulfilled, but we will do our best to contact our brand partners and vendors to check on the availability of the item for you.
          </div>
        </div>
      </div>
    </Drawer>
  </div>
  <div class="modal-back-in-stock-notification-desktop" v-else>
    <Modal :open="open" @close="emits('close')" :btn-close-visible="true">
      <div class="content">
        <div class="header">
          <div class="title">Back in stock notification</div>
          <div class="desc">Please let us know the size and color you are interested in and we will let you know once
            it's back in stock.
          </div>
        </div>
        <div class="product">
          <div class="subtitle">
            {{ product.brand }}
          </div>
          <div class="title">
            {{ product.title }}
          </div>
        </div>
        <div class="colors">
          <div class="selections">
            <div
              class="selection"
              :class="{selected: current === index}"
              v-for="(item, index) in options"
              @click="handleColorSelect(index)"
            >
              <div class="color" :style="{backgroundColor: item.data}">
                <img v-if="item.data.includes('http')" :src="item.data" alt="">
              </div>
            </div>
          </div>
          <div class="selected">
            Color: <span class="bold">{{ options[current]?.name }}</span>
          </div>
        </div>
        <div class="sizes">
          <div class="title">
            <span>Select a Size:</span>
          </div>
          <div class="selections">
            <div
              v-for="(size, index) in options[current]?.sizeList"
              class="selection"
              :class="{
                large: options[current]?.sizeList.find(size => size.value.length >= 5),
                selected: size.selected
              }"
              @click="handleSizeSelect({size, index})"
            >
              {{ size.value }}
            </div>
          </div>
        </div>
        <div class="email">
          <module-input
            type="text"
            v-model="email"
            title="Email"
            name="email"
            :error-tips="emailErrorTips"
            :error="emailError"
            @blur="handleEmailBlur"
          />
        </div>
        <div class="confirm">
          <div class="btn-primary" :class="{'btn-disabled': btnDisabled || isLoading}" @click="notify">
            <el-icon v-if="isLoading" class="is-loading" color="white" size="16" style="margin-right: 8px">
              <Loading/>
            </el-icon>
            <span>notify me</span>
          </div>
          <div class="tips">
            We cannot guarantee that back in stock requests will be fulfilled, but we will do our best to contact our brand partners and vendors to check on the availability of the item for you.
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.modal-back-in-stock-notification-mobile {
  .content {
    width: 100vw;
    padding: 14px 0 30px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header {
      width: 100%;
      padding: 0 20px;

      .title {
        width: 100%;
        padding: 16px 0;

        /* Eyebrow/medium */
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 650;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.056px;
        text-transform: uppercase;

        text-align: center;
      }

      .desc {
        text-align: center;
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.056px;
      }
    }

    .product {
      width: 100%;
      padding: 0 20px;

      .subtitle {
        /* Eyebrow/medium */
        font-family: "TWK Lausanne";
        font-weight: 650;
        font-size: 14px;
        font-style: normal;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.056px;
        text-transform: uppercase;
      }

      .title {
        /* Headline/small */
        font-family: "Alternate Gothic No3 D";
        font-size: 36px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px; /* 88.889% */
        text-transform: uppercase;
        margin-top: 12px;
      }
    }

    .colors {
      width: 100%;
      padding: 0 20px;

      display: flex;
      align-items: center;

      .selections {
        display: flex;
        margin-right: 6px;

        .selection {
          border: 0.5px $color-BNTO-beige-dark solid;

          display: flex;
          justify-content: center;
          align-items: center;

          width: 28px;
          height: 28px;
          border-radius: 50%;
          margin-right: 10px;

          cursor: pointer;

          &:last-child {
            margin-right: 0;
          }

          .color {
            width: 23px;
            height: 23px;
            border-radius: 50%;

            img {
              width: 100%;
              height: 100%;
              border-radius: 50%;
            }
          }
        }

        .selected {
          border: 1.5px $color-gray-800 solid;
        }
      }

      .selected {
        /* Text sm/Regular */
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 20px; /* 142.857% */
        color: $color-gray-700;

        .bold {
          /* Text sm/Bold */
          font-family: "TWK Lausanne";
          font-weight: 650;
          font-size: 14px;
          font-style: normal;
          line-height: 20px; /* 142.857% */
          color: $color-gray-800;

          margin-left: 6px;
        }
      }
    }

    .sizes {
      width: 100%;
      padding: 0 20px;

      .title {
        max-width: 340px;
        /* Text sm/Regular */
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 20px; /* 142.857% */

        display: flex;
        justify-content: space-between;

        .size-charts {
          font-family: "TWK Lausanne";
          font-size: 14px;
          font-style: normal;
          font-weight: 650;
          line-height: 20px; /* 142.857% */
          text-decoration-line: underline;
          cursor: pointer;
        }
      }

      .selections {
        max-width: 340px;
        margin-top: 6px;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .selection {
          width: 40px;
          height: 40px;
          border: 0.5px $color-gray-300 solid;
          display: flex;
          justify-content: center;
          align-items: center;

          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */
          cursor: pointer;

          &.large {
            width: 92px;
          }

          &:hover {
            border: 1.5px solid $color-gray-800;
            /* Text xs/Bold */
            font-family: "TWK Lausanne";
            font-size: 12px;
            font-style: normal;
            font-weight: 650;
            line-height: 18px; /* 150% */
          }

          &.selected {
            background-color: $color-gray-800;
            color: white
          }
        }

        .disabled {
          background-color: $color-gray-100;
          color: $color-gray-400;
          border: none;
          position: relative;
          cursor: not-allowed;

          img {
            position: absolute;
            top: 0;
            left: 0;
          }

          //&:after {
          //  content: "Ⅹ";
          //  font-size: 30px;
          //  font-weight: lighter;
          //  position: absolute;
          //  width: 40px;
          //  height: 40px;
          //  display: flex;
          //  justify-content: center;
          //  align-items: center;
          //}
        }

        .active {
          border: 1.5px $color-gray-800 solid;
        }
      }
    }

    .email {
      width: 100%;
      padding: 0 20px;
    }

    .confirm {
      width: 100%;
      padding: 0 20px;

      .tips {
        text-align: center;
        font-family: "TWK Lausanne";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 150% */

        margin-top: 10px;
      }
    }
  }
}

.modal-back-in-stock-notification-desktop {
  .content {
    width: 600px;
    padding: 14px 0 30px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header {
      width: 100%;
      padding: 0 20px;

      .title {
        width: 100%;
        padding: 26px 0 16px 0;
        font-family: "Alternate Gothic No3 D";
        font-size: 36px;
        font-style: normal;
        font-weight: 400;
        line-height: 22px; /* 61.111% */
        letter-spacing: -0.144px;
        text-transform: uppercase;
        text-align: center;
      }

      .desc {
        text-align: center;
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.056px;
      }
    }

    .product {
      width: 100%;
      padding: 0 20px;

      .subtitle {
        /* Eyebrow/medium */
        font-family: "TWK Lausanne";
        font-weight: 650;
        font-size: 14px;
        font-style: normal;
        line-height: 18px; /* 128.571% */
        letter-spacing: -0.056px;
        text-transform: uppercase;
      }

      .title {
        /* Headline/small */
        font-family: "Alternate Gothic No3 D";
        font-size: 36px;
        font-style: normal;
        font-weight: 400;
        line-height: 32px; /* 88.889% */
        text-transform: uppercase;
        margin-top: 12px;
      }
    }

    .colors {
      width: 100%;
      padding: 0 20px;

      display: flex;
      align-items: center;

      .selections {
        display: flex;
        margin-right: 6px;

        .selection {
          border: 0.5px $color-BNTO-beige-dark solid;

          display: flex;
          justify-content: center;
          align-items: center;

          width: 28px;
          height: 28px;
          border-radius: 50%;
          margin-right: 10px;

          cursor: pointer;

          &:last-child {
            margin-right: 0;
          }

          .color {
            width: 23px;
            height: 23px;
            border-radius: 50%;

            img {
              width: 100%;
              height: 100%;
              border-radius: 50%;
            }
          }
        }

        .selected {
          border: 1.5px $color-gray-800 solid;
        }
      }

      .selected {
        /* Text sm/Regular */
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 20px; /* 142.857% */
        color: $color-gray-700;

        .bold {
          /* Text sm/Bold */
          font-family: "TWK Lausanne";
          font-weight: 650;
          font-size: 14px;
          font-style: normal;
          line-height: 20px; /* 142.857% */
          color: $color-gray-800;

          margin-left: 6px;
        }
      }
    }

    .sizes {
      width: 100%;
      padding: 0 20px;

      .title {
        max-width: 340px;
        /* Text sm/Regular */
        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 20px; /* 142.857% */

        display: flex;
        justify-content: space-between;

        .size-charts {
          font-family: "TWK Lausanne";
          font-size: 14px;
          font-style: normal;
          font-weight: 650;
          line-height: 20px; /* 142.857% */
          text-decoration-line: underline;
          cursor: pointer;
        }
      }

      .selections {
        max-width: 340px;
        margin-top: 6px;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;

        .selection {
          width: 40px;
          height: 40px;
          border: 0.5px $color-gray-300 solid;
          display: flex;
          justify-content: center;
          align-items: center;

          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */
          cursor: pointer;

          &.large {
            width: 92px;
          }

          &:hover {
            border: 1.5px solid $color-gray-800;
            /* Text xs/Bold */
            font-family: "TWK Lausanne";
            font-size: 12px;
            font-style: normal;
            font-weight: 650;
            line-height: 18px; /* 150% */
          }

          &.selected {
            background-color: $color-gray-800;
            color: white
          }
        }

        .disabled {
          background-color: $color-gray-100;
          color: $color-gray-400;
          border: none;
          position: relative;
          cursor: not-allowed;

          img {
            position: absolute;
            top: 0;
            left: 0;
          }

          //&:after {
          //  content: "Ⅹ";
          //  font-size: 30px;
          //  font-weight: lighter;
          //  position: absolute;
          //  width: 40px;
          //  height: 40px;
          //  display: flex;
          //  justify-content: center;
          //  align-items: center;
          //}
        }

        .active {
          border: 1.5px $color-gray-800 solid;
        }
      }
    }

    .email {
      width: 100%;
      padding: 0 20px;
    }

    .confirm {
      width: 100%;
      padding: 0 20px;

      .tips {
        text-align: center;
        font-family: "TWK Lausanne";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px; /* 150% */

        margin-top: 10px;
      }
    }
  }
}
</style>
